<!-- <template>
  <div class="pdb-viewer" ref="viewerRef" v-loading="innerLoading" element-loading-text="Loading PDB..." />
</template>

<script setup>
  import { ref, watch, onMounted, computed } from "vue";
  import { get_raw_data } from "@/api/index.js";

  const props = defineProps({
    pdbText: {
      type: String,
      default: ""
    },
    pdbFilePath: {
      type: String,
      default: ""
    },
    loading: {
      type: Boolean,
      default: false
    }
  });

  const emit = defineEmits(["loaded", "error"]);

  const viewerRef = ref(null);
  let viewer = null;

  const innerLoading = ref(false);

  // 统一 loading 状态（外部 or 内部）
  const loadingState = computed(() => {
    return props.loading || innerLoading.value;
  });

  onMounted(() => {
    viewer = $3Dmol.createViewer(viewerRef.value, {
      backgroundColor: "white"
    });

    // 初始自动加载
    if (props.pdbText) {
      loadFromText(props.pdbText);
    } else if (props.pdbFilePath) {
      loadFromFile(props.pdbFilePath);
    }
  });

  /* ========= 加载逻辑 ========= */

  async function loadFromFile(filePath) {
    if (!filePath || !viewer) return;

    innerLoading.value = true;

    try {
      const params = {
        file_path: filePath,
        callback_name: "file_path",
        callback_function: { file_format: "raw_text" }
      };

      const res = await get_raw_data(params);
      loadFromText(res.data);
    } catch (e) {
      console.error("PDB file load error:", e);
      emit("error", e);
    } finally {
      innerLoading.value = false;
    }
  }

  function loadFromText(pdbText) {
    if (!pdbText || !viewer) return;

    try {
      viewer.clear();
      viewer.addModel(pdbText, "pdb");
      // viewer.setStyle({}, { cartoon: { color: "#9e9e9e" } });


      // 为所有原子添加样式，并按链 ID 自动着色
      // viewer.setStyle({}, { cartoon: { colorscheme: 'chain' } });
      viewer.setStyle({ chain: 'A' }, { cartoon: { color: 'spectrum' } });

      viewer.zoomTo();
      viewer.render();

      emit("loaded");
    } catch (e) {
      console.error("PDB text load error:", e);
      emit("error", e);
    }
  }

  /* ========= 监听 props ========= */

  watch(
    () => props.pdbText,
    (val) => {
      if (val) loadFromText(val);
    }
  );

  watch(
    () => props.pdbFilePath,
    (val) => {
      if (val) loadFromFile(val);
    }
  );
</script>

<style scoped>
  .pdb-viewer {
    width: 350px;
    height: 350px;
    /*  margin: 0 auto; */
    position: relative;
  }
</style> -->





<template>
  <div class="pdb-viewer" ref="viewerRef" v-loading="innerLoading" element-loading-text="Loading PDB..." />
</template>

<script setup>
  import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
  import { get_raw_data } from "@/api/index.js";

  const props = defineProps({
    pdbId: { type: String, default: "" },
    pdbText: { type: String, default: "" },
    pdbFilePath: { type: String, default: "" },
    loading: { type: Boolean, default: false }
  });

  const emit = defineEmits(["loaded", "error"]);
  const viewerRef = ref(null);
  let viewer = null;
  const innerLoading = ref(false);

  /* ========= 自适应逻辑 ========= */

  const handleResize = () => {
    if (viewer) {
      viewer.resize(); // 核心方法：强制 3Dmol 重新计算 canvas 大小
      viewer.render(); // 重新渲染画面
    }
  };

  onMounted(() => {
    // 初始化 viewer
    viewer = $3Dmol.createViewer(viewerRef.value, {
      backgroundColor: "white"
    });

    // 监听窗口缩放
    window.addEventListener('resize', handleResize);

    if (props.pdbId) {
      loadFromId(props.pdbId);
    } else if (props.pdbText) {
      loadFromText(props.pdbText);
    } else if (props.pdbFilePath) {
      loadFromFile(props.pdbFilePath);
    }
  });

  onBeforeUnmount(() => {
    // 销毁监听，防止内存泄漏
    window.removeEventListener('resize', handleResize);
  });


  function loadFromId(id) {
    if (!id || !viewer) return;

    viewer.clear();
    // 使用 $3Dmol.download 接口
    // 格式为 "pdb:ID"
    $3Dmol.download(`pdb:${id}`, viewer, {}, () => {
      viewer.setStyle({ chain: 'A' }, { cartoon: { color: 'spectrum' } });
      viewer.zoomTo();
      viewer.render();
      emit("loaded");
    });
  }


  /* ========= 原有加载逻辑 (保持不变) ========= */

  async function loadFromFile(filePath) {
    if (!filePath || !viewer) return;
    innerLoading.value = true;
    try {
      const params = {
        file_path: filePath,
        callback_name: "file_path",
        callback_function: { file_format: "raw_text" }
      };
      const res = await get_raw_data(params);
      loadFromText(res.data);
    } catch (e) {
      console.error("PDB file load error:", e);
      emit("error", e);
    } finally {
      innerLoading.value = false;
    }
  }

  function loadFromText(pdbText) {
    if (!pdbText || !viewer) return;
    try {
      viewer.clear();
      viewer.addModel(pdbText, "pdb");
      viewer.setStyle({ chain: 'A' }, { cartoon: { color: 'spectrum' } });
      viewer.zoomTo();
      viewer.render();
      emit("loaded");
    } catch (e) {
      console.error("PDB text load error:", e);
      emit("error", e);
    }
  }

  watch(() => props.pdbText, (val) => { if (val) loadFromText(val); });
  watch(() => props.pdbFilePath, (val) => { if (val) loadFromFile(val); });
</script>

<style scoped>
  .pdb-viewer {
    /*  width: 100%;
    height: 100%;
    min-height: 200px;
    position: relative;
    overflow: hidden; */

    width: 100%;
    height: 300px;
    background-color: #333;
    margin: 0 auto;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
  }
</style>